import { Info } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import BottomNavBar from "../../components/BottomNavBar";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const PhysiologicalPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <h1 className="text-base font-semibold text-foreground">
            Physiological Load
          </h1>
          <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
            No Data
          </span>
        </div>

        {/* Main Score Card */}
        <Card className="rounded-2xl bg-gradient-to-br from-orange-50/90 to-orange-100/50 p-4 shadow-sm dark:from-orange-950/20 dark:to-orange-900/10">
          <div className="mb-3 flex items-start gap-2">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground">
                Physiological Load
              </div>
              <div className="text-xs text-muted-foreground">
                Past 24 hours • Today's Date
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-5xl font-bold text-foreground">
              --
              <span className="text-3xl text-muted-foreground"> / 100</span>
            </div>
            <span className="mt-1.5 inline-block rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>

          <div className="mb-3 rounded-lg bg-white/40 p-3 dark:bg-gray-800/20">
            <p className="text-sm leading-relaxed text-gray-900 dark:text-gray-100">
              <span className="font-bold">No data available.</span>{" "}
              Connect your fitness device to track physiological load.
            </p>
          </div>

          <div className="mb-3 grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-white/70 p-2.5 text-center shadow-sm dark:bg-gray-800/40">
              <div className="text-[10px] leading-tight text-muted-foreground">
                HRV at baseline
              </div>
              <div className="mt-1 text-lg font-bold text-foreground">--</div>
            </div>
            <div className="rounded-lg bg-white/70 p-2.5 text-center shadow-sm dark:bg-gray-800/40">
              <div className="text-[10px] leading-tight text-muted-foreground">
                Sleep Recovery
              </div>
              <div className="mt-1 text-lg font-bold text-foreground">
                --
              </div>
            </div>
            <div className="rounded-lg bg-white/70 p-2.5 text-center shadow-sm dark:bg-gray-800/40">
              <div className="text-[10px] leading-tight text-muted-foreground">
                Training HR
              </div>
              <div className="mt-1 text-lg font-bold text-foreground">
                --
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Confidence</span>
              <span className="font-semibold text-foreground">--</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                style={{ width: "0%" }}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 rounded-xl border-border bg-background hover:bg-muted"
              onClick={() => navigate("/glucera" + GluceraNavigationRoutes.STRESS_INDEX_INPUTS)}
            >
              View Inputs
            </Button>
            <Button 
              className="flex-1 rounded-xl bg-blue-500 text-white hover:bg-blue-600"
              onClick={() => navigate("/glucera" + GluceraNavigationRoutes.IMPROVE_ACCURACY)}
            >
              Improve Accuracy
            </Button>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Data evaluation calculates how well the most current input contributes to model accuracy.
          </p>
        </Card>

        {/* Stress Index Inputs Section */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            Stress Index Inputs
          </h2>

          {/* Stress Level Card */}
          <div className="flex gap-2">
            <Card className="flex-1 rounded-xl bg-gradient-to-br from-orange-50/90 to-orange-100/50 p-3 shadow-sm dark:from-orange-950/20 dark:to-orange-900/10">
              <span className="mb-2 inline-block rounded-md bg-orange-200/80 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/50 dark:text-orange-400">
                Confidence
              </span>
              <div className="mb-1 text-3xl font-bold text-foreground">--</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                No data available
              </div>
            </Card>

            <Card className="flex-1 rounded-xl bg-gradient-to-br from-orange-50/90 to-orange-100/50 p-3 shadow-sm dark:from-orange-950/20 dark:to-orange-900/10">
              <span className="mb-2 inline-block rounded-md bg-orange-200/80 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/50 dark:text-orange-400">
                Correlation Window
              </span>
              <div className="mb-1 text-lg font-bold text-foreground">--</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                No data available
              </div>
            </Card>
          </div>

          {/* HRV Circulation */}
          <Card 
            className="cursor-pointer rounded-xl bg-white/60 p-3 shadow-sm transition-all hover:bg-white/80 dark:bg-gray-800/60 dark:hover:bg-gray-800/80"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HRV_DEVIATION)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="text-base font-bold text-foreground">
                  HRV Circulation
                </div>
                <div className="text-sm font-semibold text-foreground">
                  -- vs baseline
                </div>
              </div>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                No Data
              </span>
            </div>
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              Lower HRV relative to your baseline increases physiological load.
            </p>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: "70%" }}
              />
            </div>
          </Card>

          {/* Sleep Recovery */}
          <Card 
            className="cursor-pointer rounded-xl bg-white/60 p-3 shadow-sm transition-all hover:bg-white/80 dark:bg-gray-800/60 dark:hover:bg-gray-800/80"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.SLEEP_RECOVERY)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="text-base font-bold text-foreground">
                  Sleep Recovery
                </div>
                <div className="text-sm font-semibold text-foreground">-- hrs</div>
              </div>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                No Data
              </span>
            </div>
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              Sleep debt reduces recovery and increases stress load.
            </p>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: "100%" }}
              />
            </div>
          </Card>

          {/* Resting Heart Rate Delta */}
          <Card 
            className="cursor-pointer rounded-xl bg-white/60 p-3 shadow-sm transition-all hover:bg-white/80 dark:bg-gray-800/60 dark:hover:bg-gray-800/80"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.RESTING_HR_DELTA)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="text-base font-bold text-foreground">
                  Resting Heart Rate Delta
                </div>
                <div className="text-sm font-semibold text-foreground">-- bpm</div>
              </div>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                No Data
              </span>
            </div>
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              Higher resting HR can indicate sympathetic activation.
            </p>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: "100%" }}
              />
            </div>
          </Card>

          {/* Activity Recovery Buffer */}
          <Card 
            className="cursor-pointer rounded-xl bg-white/60 p-3 shadow-sm transition-all hover:bg-white/80 dark:bg-gray-800/60 dark:hover:bg-gray-800/80"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.ACTIVITY_RECOVERY)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="text-base font-bold text-foreground">
                  Activity Recovery Buffer
                </div>
                <div className="text-sm font-semibold text-foreground">
                  -- active recovery
                </div>
              </div>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                No Data
              </span>
            </div>
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              Light movement can modulate better physiological load.
            </p>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: "100%" }}
              />
            </div>
          </Card>
        </div>

        {/* Info Box */}
        <Card className="rounded-xl bg-blue-50/80 p-3 shadow-sm dark:bg-blue-900/20">
          <div className="flex gap-2">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
              <Info className="h-3 w-3 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-foreground">
                Your Stress Index is calculated from HRV deviation, sleep
                quality trends, relative effort, stress adjustment is applied.
              </span>{" "}
              Continuous trends have been analyzed. Improving trends to be more metabolically
              sound could help upregulate recovery signals and overall heart rate variability.
            </div>
          </div>
        </Card>

        {/* Without Fitbit Section */}
        <Card className="rounded-xl bg-white/60 p-4 text-center shadow-sm dark:bg-gray-800/60">
          <div className="mb-3 flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                <Info className="h-3 w-3 text-gray-600 dark:text-gray-400" />
              </div>
              <span className="text-sm font-bold text-foreground">
                Without Fitbit
              </span>
            </div>
            <span className="rounded-md bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-400">
              Unavailable
            </span>
          </div>
          <div className="mb-3 flex items-center justify-center gap-1">
            <span className="text-sm text-muted-foreground">Bite Balance Stress Index™</span>
          </div>
          <div className="mb-1 text-3xl font-bold text-muted-foreground">
            --<span className="text-2xl"> / 100</span>
          </div>
          <div className="mb-3 text-xs text-muted-foreground">Sync needed</div>
          <p className="mb-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            <span className="font-semibold text-foreground">No signals yet.</span>{" "}
            Connect Fitbit or enable permissions to see physiological data.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 rounded-xl border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Learn More
            </Button>
            <Button className="flex-1 rounded-xl bg-blue-500 text-white hover:bg-blue-600">
              Connect Fitbit
            </Button>
          </div>
        </Card>
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar activeIndex={3} />
    </div>
  );
};

export default PhysiologicalPage;
